import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import Head from 'next/head';
import Layout from '../../components/layout';
import NavBar from '../../components/navbar';
import Container from '../../components/container';
import MatchLiveData from '../../components/match-live-data';
import config from '../../lib/config';

import { GetStaticProps, GetStaticPaths } from 'next';
import { fetchAPI, fetchData, getPrimaryMenuItems } from '../../lib/api';
import { ISponsor } from '../../models/sponsor';
import { IMenuItem } from '../../models/menu-item';
import { MatchCard } from '../../components/match-card';
import { IMatch } from '../../models/match';
import { H3, H4 } from '../../components/text';
import { CgSpinnerAlt } from 'react-icons/cg';
import { fetcher } from '@/utils/fetcher';
import ScoreCard from '@/components/score-card';
import MatchStats from '@/components/match-stats';
import { IconButton } from '@/components/icon-button';
import Icon from '@/components/icon/Icon';
import RoundsTable from '@/components/rounds-table';
import Link from 'next/link';
import { IMatchRoundData } from '@/models/matchRoundData';
import Countdown from '@/components/countdown';
import MediaObject from '@/components/media-object';
import MatchLineup from '@/components/match-lineup';
import Tabs from '@/components/tabs';
import classNames from 'classnames';
import Bugsnag from '@bugsnag/js';
import { useViewportWidth } from '@/utils/useViewPortWidth';
import HeadToHeadTab from '@/components/head-to-head-tab';

interface IProps {
	primaryMenuItems: IMenuItem[];
	sponsors: ISponsor[];
	initialMatchData: IMatch;
	slug: string;
	matchRoundsData: IMatchRoundData;
}
interface MatchHeaderProps {
	justify?: 'center' | 'between';
}

export default function MatchPage({ primaryMenuItems, sponsors, initialMatchData, slug, matchRoundsData }: IProps) {
	const [matchData, setMatchData] = useState<IMatch>(initialMatchData);
	const [roundData, setRoundData] = useState<IMatchRoundData | null>(null);
	const [currentRound, setCurrentRound] = useState(matchRoundsData?.currentRound);

	const { width } = useViewportWidth();

	useEffect(() => {
		const fetchRoundData = async () => {
			try {
				const response = await fetch(
					`${config.apiUrl}/wp-json/statsperform/v1/round-schedule?round=${currentRound}`,
				);
				const data = await response.json();
				setRoundData(data);
			} catch (error) {
				console.error('Error fetching round data:', error);
			}
		};
		fetchRoundData();
	}, [currentRound]);

	const refreshInterval = matchData.status === 'Playing' ? 60000 : 0;

	const { data, error } = useSWR(`${config.apiUrl}/wp-json/statsperform/v1/match/${slug}`, fetcher, {
		fallbackData: initialMatchData,
		refreshInterval,
		revalidateOnFocus: false,
	});

	useEffect(() => {
		if (data) {
			setMatchData(data); // Update state with the new data from SWR
		}
	}, [data]);

	if (error) {
		Bugsnag.notify(error);
		return <div>Er ging iets mis bij het laden van de pagina, ververs de pagina</div>;
	}

	if (!data) {
		return (
			<div className="flex flex-col items-center h-full fixed w-full justify-center ">
				<CgSpinnerAlt className="animate-spin text-black w-16 h-16" />
			</div>
		);
	}

	const desiredStatTypes = [
		'totalScoringAtt',
		'ontargetScoringAtt',
		'totalOffside',
		'cornerTaken',
		'fkFoulLost',
		'possessionPercentage',
	];

	const MatchHeader = ({ justify = 'center' }: MatchHeaderProps) => {
		const justifyClass = justify === 'center' ? 'justify-center' : 'justify-between';
		if (!matchData.live_data?.lineUp) return null;

		const hyphenFormation = (lineUpIndex: number) => {
			const formation = matchData.live_data?.lineUp?.[lineUpIndex]?.formationUsed || '';
			return formation.split('').join('-');
		};

		const formationHome = hyphenFormation(0);
		const formationAway = hyphenFormation(1);

		return (
			<section className="mb-4">
				<div className={classNames('flex', justifyClass)}>
					<div className="flex-1">
						<MediaObject
							title={matchData.homeContestant.contestantClubName}
							imageUrl={matchData.homeContestant.contestantLogo || ''}
							subtitle={formationHome}
							mediaAlignment="right"
						/>
					</div>
					<div className="flex-1">
						<MediaObject
							title={matchData.awayContestant.contestantClubName}
							imageUrl={matchData.awayContestant.contestantLogo || ''}
							subtitle={formationAway}
							mediaAlignment="left"
						/>
					</div>
				</div>
			</section>
		);
	};

	const TABS = [
		{
			label: 'Head-to-Head',
			children: (
				<section className="pb-24">
					{matchData.homeContestant?.contestantId && matchData.awayContestant?.contestantId ? (
						<HeadToHeadTab
							homeContestantId={matchData.homeContestant.contestantId}
							awayContestantId={matchData.awayContestant.contestantId}
							homeTeamName={matchData.homeContestant.contestantClubName}
							awayTeamName={matchData.awayContestant.contestantClubName}
						/>
					) : (
						<H3 className="text-center py-6">Head-to-head gegevens niet beschikbaar</H3>
					)}
				</section>
			),
		},
		{
			label: 'Opstellingen',
			children: (
				<section className="pb-24">
					{width > 992 ? <MatchHeader /> : null}
					{matchData.live_data?.lineUp?.[1] ? (
						<MatchLineup
							homeLineup={matchData.live_data.lineUp[0]}
							awayLineup={matchData.live_data.lineUp[1]}
						/>
					) : (
						<H3 className="text-center py-6">Nog geen opstelling beschikbaar</H3>
					)}
				</section>
			),
		},
		{
			label: 'Wedstrijdverloop',
			children: (
				<section className="pb-24">
					<MatchHeader />
					{matchData.live_data?.lineUp && matchData.live_data.lineUp.length > 0 ? (
						<MatchLiveData matchData={matchData} />
					) : (
						<H3 className="text-center py-6">Nog geen opstelling beschikbaar</H3>
					)}
				</section>
			),
		},
		{
			label: 'Statistieken',
			children: (
				<section className="pb-24">
					<MatchHeader />
					{matchData.live_data && (
						<div className="mx-auto max-w-xl">
							<MatchStats lineUp={matchData.live_data.lineUp} desiredStatTypes={desiredStatTypes} />
						</div>
					)}
				</section>
			),
		},
	];

	const pageTitle = `${matchData.title} | ${config.siteName}`;
	const meta = matchData.title ? `${matchData.title}` : 'Wedstrijden van de Keuken Kampioen Divisie';

	return (
		<Layout preview sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />

			<Container className="relative py-12 xl:py-16">
				<div className="flex flex-col lg:flex-row">
					<div className="hidden xl:block w-full lg:w-4/12 xl:w-3/12  mb-12 h-full">
						<div className="bg-black-500">
							<Countdown matchRoundsData={matchRoundsData} />

							{roundData && (
								<div className="w-full h-full relative pb-4">
									<div className="swiper-title pb-4 flex flex-row justify-center items-center">
										<IconButton
											icon="arrow-left"
											className="mr-2 bg-transparent"
											onClick={() => setCurrentRound(roundData.prevRound)}
											disabled={!roundData.prevRound}
										/>
										<H4 className="text-white mx-1 lg:mx-3 text-base lg:text-lg xl:text-xl text-center uppercase">
											Speelronde {roundData.currentRound}
										</H4>
										<IconButton
											icon="arrow-right"
											className="ml-2 bg-transparent"
											onClick={() => setCurrentRound(roundData.nextRound)}
											disabled={!roundData.nextRound}
										/>
									</div>

									<RoundsTable matchesData={roundData} snug />

									<div className="my-8 flex justify-center items-center">
										<Link href={'/programma'} className="flex items-center">
											<span className="inline-block mr-3 text-red-500 hover:text-red-700 font-inter font-bold uppercase tracking-wide text-base lg:text-lg">
												Alle wedstrijden
											</span>
											<Icon icon={'arrow-right'} color="#E2013D" size={18} />
										</Link>
									</div>
								</div>
							)}
						</div>
					</div>
					<div className="flex-1 lg:pl-6 xl:pl-16 w-full lg:w-8/12 xl:w-9/12">
						<section className="mb-6">
							<MatchCard
								venue={matchData.venue}
								date={matchData.date}
								time={matchData.time}
								homeContestant={matchData.homeContestant}
								awayContestant={matchData.awayContestant}
								status={matchData.status}
								scores={matchData.scores}
								channel={matchData.channel}
							/>
							{matchData.live_data?.lineUp && matchData.live_data.lineUp.length > 0 && (
								<ScoreCard liveData={matchData.live_data} matchData={matchData} />
							)}
						</section>
						<Tabs tabs={TABS} size={'sm'} tabColorInterted={false} selectedColor="red-500" />
					</div>
				</div>
			</Container>
		</Layout>
	);
}

const fetchRoundMatchData = async () => {
	const matchRoundsData = `${config.apiUrl}/wp-json/statsperform/v1/current-round`;
	return fetchData(matchRoundsData);
};

export const getStaticPaths: GetStaticPaths = async () => {
	return { paths: [], fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async ({ params, preview = false }) => {
	const primaryMenuItems = await getPrimaryMenuItems();

	const pageData = await fetchAPI<{ sponsors: { nodes: any[] } }>(
		`
		query {
			sponsors(first: 20) {
				nodes {
					title
					sponsor_fields {
						subtitle
						websiteUrl
						mainSponsor
						invertedImage {
							sourceUrl
						}
					}
				}
			}
		}
	`,
	);

	const slug = params?.slug;

	// NOTE: every early return here must carry `revalidate`. Without it Next caches
	// the result until the next deploy, so a single upstream hiccup permanently
	// freezes a 404 on that URL. Transient failures throw instead of returning
	// `notFound`, because an uncached 500 is retried on the next request.
	const endpoint = `${config.apiUrl}/wp-json/statsperform/v1/match/${slug}`;
	const res = await fetch(endpoint);

	if (res.status === 404) {
		return {
			notFound: true,
			revalidate: 300,
		};
	}

	if (!res.ok) {
		throw new Error(`Match API responded ${res.status} for slug "${slug}"`);
	}

	const matchData: IMatch | null = await res.json();

	if (!matchData) {
		return {
			notFound: true,
			revalidate: 300,
		};
	}

	let matchRoundsData: IMatchRoundData | null = null;

	try {
		matchRoundsData = await fetchRoundMatchData();
	} catch (error) {
		console.error('Error fetching data:', error);
	}

	return {
		props: {
			primaryMenuItems,
			sponsors: pageData.sponsors.nodes,
			slug,
			initialMatchData: matchData,
			matchRoundsData,
		},
		revalidate: 300,
	};
};
